import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertCircle, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { Task, isTaskSyncedWithJira, formatJiraSyncDate, buildJiraIssueUrl } from '../types/task';
import { cn } from '../utils/cn';
import { getProject } from '../services/api';

interface JiraBadgeProps {
  task: Task;
  jiraUrl?: string;
  size?: 'sm' | 'default' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export const JiraBadge: React.FC<JiraBadgeProps> = ({
  task,
  jiraUrl,
  size = 'sm',
  showLabel = true,
  className
}) => {
  // Se não tem dados JIRA, não renderiza
  if (!task.jiraIssueKey) {
    return null;
  }

  const isSynced = isTaskSyncedWithJira(task);
  const issueKey = task.jiraIssueKey;
  const lastSync = task.lastJiraSync;

  // Define variante da badge baseada no status
  const getBadgeVariant = () => {
    if (!isSynced) return 'outline';
    return 'info'; // Badge azul para issues sincronizadas
  };

  // Constrói URL do JIRA se disponível
  const jiraIssueUrl = jiraUrl ? buildJiraIssueUrl(jiraUrl, issueKey) : null;
  

  // Conteúdo da badge
  const badgeContent = (
    <div className="flex items-center gap-1">
      <ExternalLink className="w-3 h-3" />
      {showLabel && (
        <span className="font-mono text-xs">
          {issueKey}
        </span>
      )}
    </div>
  );

  // Tooltip de informações
  const tooltipText = isSynced && lastSync
    ? `JIRA: ${issueKey}\nÚltimo sync: ${formatJiraSyncDate(lastSync)}\nClique para abrir no JIRA`
    : `JIRA: ${issueKey}\nClique para abrir no JIRA`;

  // Se tem URL do JIRA, torna clicável
  if (jiraIssueUrl) {
    return (
      <a
        href={jiraIssueUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={tooltipText}
        className={cn(
          "transition-all duration-200 hover:scale-105 hover:shadow-sm inline-block z-10 relative",
          className
        )}
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()} // Evita interferir com cliques no card
      >
        <Badge 
          variant={getBadgeVariant()} 
          size={size}
          className="cursor-pointer hover:bg-blue-500/30 border-blue-500/40"
        >
          {badgeContent}
        </Badge>
      </a>
    );
  }

  // Badge sem link (caso não tenha URL do JIRA)
  return (
    <div
      title={tooltipText}
      className={className}
    >
      <Badge 
        variant={getBadgeVariant()} 
        size={size}
        className="opacity-75"
      >
        {badgeContent}
      </Badge>
    </div>
  );
};

// Componente de status de sincronização (para uso em detalhes)
export const JiraSyncStatus: React.FC<{ task: Task }> = ({ task }) => {
  if (!task.jiraIssueKey) {
    return null;
  }

  const isSynced = isTaskSyncedWithJira(task);
  const lastSync = task.lastJiraSync;

  if (!isSynced || !lastSync) {
    return (
      <div className="flex items-center gap-2 text-yellow-400 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>Aguardando sincronização</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-green-400 text-sm">
      <Clock className="w-4 h-4" />
      <span>Sincronizado em {formatJiraSyncDate(lastSync)}</span>
    </div>
  );
};

// Hook para buscar URL do JIRA do projeto
export const useProjectJiraUrl = (projectId: string): string | undefined => {
  const [jiraUrl, setJiraUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchProjectJiraConfig = async () => {
      try {
        const response = await getProject(projectId);
        const project = response.data.data.project;
        
        if (project.jiraEnabled && project.jiraConfig?.jiraUrl) {
          setJiraUrl(project.jiraConfig.jiraUrl);
        } else {
          // Para teste enquanto não há configuração JIRA no projeto
          // TODO: Remover após configuração JIRA estar funcionando
          setJiraUrl('https://issues.apache.org/jira');
        }
      } catch (error) {
        console.error('Error fetching project JIRA config:', error);
        setJiraUrl(undefined);
      }
    };

    if (projectId) {
      fetchProjectJiraConfig();
    }
  }, [projectId]);

  return jiraUrl;
};